import _ from 'lodash';
import Vue from 'vue';
import {AxiosResponse} from 'axios';
import {mapActions, mapGetters, mapState} from 'vuex';
import {AlertNotificationModuleConstant, EmployeeProfileModuleConstant, EventConstant, PageDriverConstant, RegularExpressionConstant, SectionConstant, UrlModuleConstant} from '../../lib/constants';
import {coachService} from '../../lib/services/coachService';
import {memberSearchService} from '../../lib/services/memberSearchService';
import TimeZoneService from '../../lib/services/timeZoneService';
import PtClientListPartial from './ptClientList.html';
import EcCommon from '../../../types/ec/ecCommon';
import EcMemberSearch from '../../../types/ec/ecMemberSearch';
import PtClientList from '../../../types/ec/ecPtClientList';
import EventHub from '../../lib/eventHub';

interface IComponentData {
	clientList: PtClientList.IClientSummary[];
	coachService: EcCommon.ICoachService;
	initializing: boolean;
	memberSearchService: EcMemberSearch.IMemberSearchService;
	searchTerm: string;
}

export default Vue.component('ecPtClientList', {
	template: PtClientListPartial,
	data(): IComponentData {
		return {
			clientList: [],
			coachService,
			initializing: true,
			memberSearchService,
			searchTerm: '',
		};
	},
	computed: {
		errorMessage(): string {
			let errorMessage: string = '';
			if (this.coachService.errorMessage) {
				errorMessage = this.coachService.errorMessage;
			}

			return errorMessage;
		},
		filteredClients(): PtClientList.IClientSummary[] {
			if (this.searchTerm.length >= 3) {
				return _.sortBy(_.filter(this.clientList, (client) => {
					return (client.firstName.toLowerCase() + ' ' + client.lastName.toLowerCase()).indexOf(this.searchTerm.toLowerCase()) >= 0 || client.memberId === Number(this.searchTerm);
				}), ['firstName', 'lastName']);
			} else {
				return this.clientList;
			}
		},
		loading(): boolean {
			return this.initializing || this.coachService.loading;
		},
		...mapGetters(EmployeeProfileModuleConstant.name,  {
			getIsEmployeeProfileValid: EmployeeProfileModuleConstant.getter.getIsValid,
		}),
		...mapGetters(UrlModuleConstant.name, [UrlModuleConstant.getter.getGlobalPageDriverUrl]),
		...mapState(EmployeeProfileModuleConstant.name, [EmployeeProfileModuleConstant.state.employeeProfile]),
	},
	methods: {
		...mapActions(AlertNotificationModuleConstant.name, [AlertNotificationModuleConstant.action.open]),
		...mapActions(AlertNotificationModuleConstant.name, {
			synchronizeAlertNotificationStore: AlertNotificationModuleConstant.action.synchronize,
		}),
		...mapActions(EmployeeProfileModuleConstant.name, {
			synchronizeEmployeeProfileStore: EmployeeProfileModuleConstant.action.synchronize,
		}),
		...mapActions(UrlModuleConstant.name, {
			synchronizeUrlStore: UrlModuleConstant.action.synchronize,
		}),
		clearTerm() {
			this.searchTerm = '';

			this.$nextTick(() => {
				const el = this.$refs.clientListSearchText as HTMLElement;
				el.click();
				el.focus();
			});
		},
		getClientLists(ignoreLoading: boolean): Promise<void> {
			return coachService.getClientList(this.employeeProfile.partyId, ignoreLoading)
				.then((response: AxiosResponse<PtClientList.IClientSummary[]>) => {
					_.forEach(response.data, (c) => {
						c.photo = '/content/dam/employee/images/profile_blank.svg';
						c.dateOfBirth = TimeZoneService.convertToLocal(c.dateOfBirth, c.timeZone);
					});

					this.clientList = _.sortBy(response.data, ['firstName', 'lastName']);
					const memberPhotoSearch: EcMemberSearch.ISearchMemberPhotoRequestObject = {
						memberIds: _.map(this.clientList, (client) => {
							return client.memberId;
						}),
					};

					if (memberPhotoSearch.memberIds.length > 0) {
						return memberSearchService.searchMemberPhotos(memberPhotoSearch, true)
							.then((photoResponse: AxiosResponse<EcMemberSearch.ISearchMemberPhotoResponse>) => {
								_.forEach(photoResponse.data.memberPhotos, (memberPhoto) => {
									const member: PtClientList.IClientSummary | undefined = _.find(this.clientList, (c) => {
										return c.memberId === memberPhoto.memberId;
									});
									if (member) {
										const photo = memberPhoto.photo ? memberPhoto.photo : '/content/dam/employee/images/profile_blank.svg';
										Vue.set(member, 'photo', photo);
									}
								});
							});
					}
				});
		},

	},
	created(): void {
		EventHub.$on(EventConstant.GetClientLists, (clientPartyId: number) => {
			this.clientList.splice(_.findIndex(this.clientList, { partyId: clientPartyId }), 1);
		});

		if (window.__PRERENDER_INJECTED || RegularExpressionConstant.authorRegex.test(window.location.href)) {
			return;
		}

		Promise.all([this.synchronizeAlertNotificationStore(), this.synchronizeEmployeeProfileStore(), this.synchronizeUrlStore()]).then(() => {
			if (!this.getIsEmployeeProfileValid) {
				window.location.assign(this.getGlobalPageDriverUrl(SectionConstant.global, PageDriverConstant.loginResource));
			}
			this.getClientLists(false);
		}).finally(() => {
			this.initializing = false;
		});
	},
});
